import React, { useState } from "react";
import {
  Alert,
  Box,
  Container,
  CssBaseline,
  Paper,
  Stack,
  ThemeProvider,
  Typography,
  createTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import FileUpload from "./components/FileUpload";
import LoadingScreen from "./components/LoadingScreen";
import OcrButton from "./components/OcrButton";
import StatusPage from "./components/StatusPage";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_API_URL || "https://lipikar.cse.iitd.ac.in/api/ocr";
const DEFAULT_LANGUAGE = "universal_iitd";

function Home() {
  const language = DEFAULT_LANGUAGE; // Indic OCR (universal_iitd)
  const documentParser = "focusformer"; // Fixed to focusformer
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  const handleFileUpload = (uploadedFiles) => {
    setFiles(uploadedFiles);
    if (uploadedFiles.length > 0) {
      setErrorMessage("");
      setInfoMessage("");
    }
  };

  const handleSubmit = async () => {
    if (!files.length) {
      setErrorMessage("Please upload a file before starting OCR.");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");
    setInfoMessage("");

    const formData = new FormData();

    if (files.length === 1 && files[0].type === "application/pdf") {
      formData.append("file_type", "pdf");
      formData.append("file", files[0]);
    } else {
      formData.append("file_type", "images");
      files.forEach((file) => {
        formData.append("file", file);
      });
    }

    formData.append("language", language);

    try {
      const response = await fetch(
        `${BACKEND_URL}/upload/?document_parser=${documentParser}&parsing_postprocessor=no_postprocessor&text_recognizer=${language}`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        const queuePosition = result.result?.upload?.queuePosition;
        if (queuePosition) {
          setInfoMessage(`Upload accepted. Your current queue position is ${queuePosition}.`);
        }
        setIsLoading(false);
        window.open(`/viewer/status/${result.result.upload.inferenceID}`, "_blank");
        return;
      }

      if (response.status === 503) {
        setErrorMessage(
          result?.error?.message || "Queue is full right now. Please upload later."
        );
      } else {
        setErrorMessage(result?.error?.message || "File upload failed. Please try again.");
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      setErrorMessage("Error uploading files. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="app-shell">
      {isLoading && <LoadingScreen />}

      <header className="hero-header">
        <div className="logo-wrap">
          <img src="/viewer/lipi_logo.png" alt="Lipikar" className="hero-logo" />
        </div>
      </header>

      <Container maxWidth="xl" className="home-container">
        <div className="hero-grid">
          <motion.section
            className="hero-panel"
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <Paper elevation={0} className="upload-panel">
              <Stack spacing={3}>
                <div>
                  <Typography variant="h5" className="panel-title">
                    New OCR Request
                  </Typography>
                  <Typography className="panel-subtitle">
                    Upload a PDF or image to start OCR processing.
                  </Typography>
                </div>

                {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
                {infoMessage ? <Alert severity="info">{infoMessage}</Alert> : null}

                <div className="input-column upload-column">
                  <div className="section-heading">
                    <Typography className="field-label">Files</Typography>
                    <Typography className="field-support">
                      Drag files in or click the upload area to browse from your device.
                    </Typography>
                  </div>
                  <FileUpload
                    onFileUpload={handleFileUpload}
                    onValidationError={setErrorMessage}
                  />
                </div>

                <div className="upload-footer">
                  {/* <Alert severity="success" className="hint-card">
                    Parser: <strong>FocusFormer</strong> | Language: <strong>Indic OCR</strong>
                  </Alert> */}

                  <Box className="action-row">
                    <OcrButton
                      handleSubmit={handleSubmit}
                      disabled={!files.length}
                      isLoading={isLoading}
                    />
                  </Box>
                </div>
              </Stack>
            </Paper>
          </motion.section>
        </div>
      </Container>
    </div>
  );
}

function App() {
  const theme = createTheme({
    palette: {
      mode: "light",
      primary: {
        main: "#006d77",
      },
      secondary: {
        main: "#9c6644",
      },
      background: {
        default: "#f4efe6",
        paper: "#fffdf9",
      },
    },
    typography: {
      fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
    },
    components: {
      MuiSelect: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            backgroundColor: "#fffdf9",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 16,
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router basename="/viewer">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/status/:id" element={<StatusPage />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
